import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, ExternalLink, Download, PlayCircle, ChevronRight, Languages } from "lucide-react";
import { getBook, getBooks } from "@/lib/api";
import { BookCover } from "@/components/BookCover";
import { TrailerEmbed } from "@/components/TrailerEmbed";
import { ReserveModal } from "@/components/ReserveModal";
import { SITE } from "@/data/site";
import useSEO from "@/hooks/useSEO";
import { useLang } from "@/context/LanguageContext";

export default function BookDetail() {
  const { t } = useLang();
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edition, setEdition] = useState(null);
  const [reserveOpen, setReserveOpen] = useState(false);
  useSEO({
    title: book?.seo_title || book?.title,
    description: book?.seo_meta_description || book?.short_description,
    canonical: book?.seo_canonical_url,
    socialImage: book?.seo_social_image,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getBook(slug)
      .then((b) => {
        if (cancelled) return;
        setBook(b);
        setEdition((b.editions && b.editions[0]) || "English");
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    getBooks()
      .then(data => { if (!cancelled) setRelated(data); })
      .catch(() => { /* related list is optional */ });
    return () => { cancelled = true; };
  }, [slug]);

  // Resolve current edition metadata; fall back to book-level defaults so
  // titles without editions_meta continue to work with the same template.
  const view = useMemo(() => {
    if (!book) return null;
    const meta = (book.editions_meta && edition && book.editions_meta[edition]) || {};
    return {
      title: meta.title || book.title,
      subtitle: meta.subtitle ?? book.subtitle,
      short_description: meta.short_description || book.short_description,
      long_description: meta.long_description || book.long_description,
      cover_accent: meta.cover_accent || book.cover_accent,
      cover_url: meta.cover_url || book.cover_url,
      isbn: meta.isbn,
      amazon_url: meta.amazon_url ?? book.amazon_url,
      green_mall_url: meta.green_mall_url ?? book.green_mall_url,
      goodreads_url: meta.goodreads_url ?? book.goodreads_url,
      sample_pdf_url: meta.sample_pdf_url ?? book.sample_pdf_url,
      trailer_url: meta.trailer_url ?? book.trailer_url,
      trailer_thumbnail_url: meta.trailer_thumbnail_url ?? book.trailer_thumbnail_url,
    };
  }, [book, edition]);

  if (loading) return <div className="container-wide py-24 font-heading text-2xl" data-testid="book-loading">Loading…</div>;
  if (!book) return <div className="container-wide py-24 font-heading text-2xl" style={{ color: "var(--gi-maroon)" }} data-testid="book-notfound">Book not found.</div>;

  const isPlaceholder = (v) => !v;
  const hasMultipleEditions = (book.editions?.length || 0) > 1;

  return (
    <div data-testid={`book-detail-${book.slug}`}>
      <section className="container-wide pt-12 pb-4">
        <Link to="/books" className="btn-ghost" data-testid="book-back-btn"><ArrowLeft size={14} /> All Books</Link>
      </section>

      <section className="container-wide grid gap-12 lg:gap-16 lg:grid-cols-[380px_1fr] pb-24">
        <div className="lg:sticky lg:top-24 lg:self-start">
          <BookCover title={view.title} accent={view.cover_accent} series={book.series} coverUrl={view.cover_url} />

          {hasMultipleEditions && (
            <div className="mt-8 border p-5" style={{ borderColor: "var(--gi-border)", background: "var(--gi-ivory-2)" }} data-testid="edition-selector">
              <div className="flex items-center gap-2 mb-4">
                <Languages size={14} style={{ color: "var(--gi-maroon)" }} />
                <span className="font-accent text-[10px]" style={{ color: "var(--gi-maroon)" }}>Choose Language Edition</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {book.editions.map((ed) => (
                  <button
                    key={ed}
                    onClick={() => setEdition(ed)}
                    className="py-2.5 text-xs font-medium border transition-colors"
                    style={{
                      borderColor: edition === ed ? "var(--gi-maroon)" : "var(--gi-border)",
                      background: edition === ed ? "var(--gi-maroon)" : "#FFFFFF",
                      color: edition === ed ? "var(--gi-ivory)" : "var(--gi-charcoal)",
                      letterSpacing: "0.06em",
                    }}
                    data-testid={`edition-${ed.toLowerCase()}`}
                  >
                    {ed}
                  </button>
                ))}
              </div>
              <p className="mt-4 text-[11px] leading-relaxed" style={{ color: "var(--gi-muted)" }}>
                Each edition has its own cover, description, ISBN, sample PDF, trailer and purchase links. Editions awaiting verified content are shown as clearly labelled placeholders.
              </p>
            </div>
          )}

          <div className="mt-8 space-y-3">
            {book.reservations_enabled && book.reservation_availability_status !== "closed" && (
              <button
                type="button"
                onClick={() => setReserveOpen(true)}
                className="btn-maroon w-full justify-center"
                data-testid="reserve-print-btn"
              >
                {book.reservation_cta_label || "Reserve Print Copy"} <ArrowRight size={14} />
              </button>
            )}
            <PurchaseBtn
              testId="buy-amazon"
              label="Buy on Amazon"
              href={view.amazon_url}
              variant="primary"
              placeholder={isPlaceholder(view.amazon_url)}
            />
            <PurchaseBtn
              testId="buy-green-mall"
              label="Buy from Green Mall"
              href={view.green_mall_url}
              variant="primary"
              placeholder={isPlaceholder(view.green_mall_url)}
            />
            <PurchaseBtn
              testId="view-goodreads"
              label="View on Goodreads"
              href={view.goodreads_url}
              variant="gold"
              placeholder={isPlaceholder(view.goodreads_url)}
            />
            <PurchaseBtn
              testId="download-sample"
              label="Download Sample PDF"
              href={view.sample_pdf_url}
              variant="outline"
              icon={<Download size={14} />}
              placeholder={isPlaceholder(view.sample_pdf_url)}
            />
          </div>
          <p className="mt-6 text-xs leading-relaxed" style={{ color: "var(--gi-muted)" }}>
            External purchase links open in a new tab. Buttons marked <em>Awaiting publisher URL</em> are editable placeholders that will activate once the publisher supplies verified links for this edition.
          </p>
        </div>

        <div>
          <div className="font-accent text-[10px] flex flex-wrap items-center gap-2" style={{ color: book.series === "Green Guru Knowledge Series" ? "var(--gi-green)" : "var(--gi-gold)" }} data-testid="book-series-label">
            <span>{book.series || "Glories of India Series"}</span>
            <span style={{ color: "var(--gi-divider)" }}>·</span>
            <span data-testid="book-status">{book.status}</span>
            <span style={{ color: "var(--gi-divider)" }}>·</span>
            <span>{edition || book.editions?.[0]} Edition</span>
            {view.isbn && (
              <>
                <span style={{ color: "var(--gi-divider)" }}>·</span>
                <span>ISBN {view.isbn}</span>
              </>
            )}
          </div>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] break-words" style={{ color: "var(--gi-maroon)" }} data-testid="book-title">{view.title}</h1>
          {view.subtitle && <div className="mt-3 font-heading text-lg sm:text-xl italic" style={{ color: "var(--gi-charcoal-2)" }} data-testid="book-subtitle">{view.subtitle}</div>}
          <div className="mt-4 text-sm" style={{ color: "var(--gi-muted)" }}>By {SITE.founder}</div>
          {book.co_author && (
            <div className="text-sm" style={{ color: "var(--gi-muted)" }} data-testid="book-co-author">With {book.co_author}</div>
          )}
          <div className="hair-divider my-10 max-w-md" />

          <Block title={t("book.overview")}>
            <p data-testid="book-overview">{view.short_description}</p>
          </Block>
          <Block title="Detailed Description">
            <p data-testid="book-long-description">{view.long_description}</p>
          </Block>
          <Block title={t("book.toc")}>
            <ul className="space-y-2 text-sm">
              {(book.table_of_contents?.length ? book.table_of_contents : [
                "[Editable chapter title placeholder — 1]",
                "[Editable chapter title placeholder — 2]",
                "[Editable chapter title placeholder — 3]",
              ]).map((c, i) => (
                <li key={`toc-${i}-${c}`} className="flex items-baseline gap-3"><span className="font-accent text-[10px]" style={{ color: "var(--gi-gold)" }}>{String(i + 1).padStart(2, "0")}</span> <span>{c}</span></li>
              ))}
            </ul>
          </Block>
          {book.what_readers_learn?.length > 0 && (
            <Block title={t("book.what_learn")}>
              <ul className="space-y-2 text-sm list-none" data-testid="book-learn-list">
                {book.what_readers_learn.map((r, i) => <li key={`learn-${i}-${r}`}>◆ {r}</li>)}
              </ul>
            </Block>
          )}
          {(() => {
            // Filter Practical Topics to publishable items only.
            // Rules: (1) non-empty, (2) not a bracketed placeholder like "[Editable …]",
            // (3) not a generic seed line ("Reading textbooks critically" etc.).
            const GENERIC = new Set([
              "reading textbooks critically",
              "asking better questions",
              "checking sources",
              "using primary evidence",
              "engaging different viewpoints",
              "distinguishing fact from opinion",
            ]);
            const publishable = (book.practical_topics || []).filter(t => {
              const s = (t || "").trim();
              if (!s) return false;
              if (/^\[.*\]$/.test(s)) return false;
              return !GENERIC.has(s.toLowerCase());
            });
            if (publishable.length === 0) return null;
            return (
              <Block title={t("book.practical_topics")}>
                <ul className="space-y-2 text-sm list-none" data-testid="book-practical-topics">
                  {publishable.map((r, i) => <li key={`practical-${i}-${r}`}>◆ {r}</li>)}
                </ul>
              </Block>
            );
          })()}
          {book.why_read?.length > 0 && !book.what_readers_learn?.length && !book.practical_topics?.length && (
            <Block title={t("book.why_read")}>
              <ul className="space-y-2 text-sm list-none">
                {book.why_read.filter(r => (r || "").trim() && !/^\[.*\]$/.test(r)).map((r, i) => <li key={`why-${i}-${r}`}>◆ {r}</li>)}
              </ul>
            </Block>
          )}
          {(book.research_themes?.length || book.topics?.length) > 0 && (
            <Block title={t("book.research_themes")}>
              <div className="flex flex-wrap gap-2">
                {(book.research_themes?.length ? book.research_themes : book.topics || []).filter(t => (t || "").trim()).map(t => (
                  <span key={t} className="text-xs px-3 py-1 border" style={{ borderColor: "var(--gi-border)", color: "var(--gi-maroon)", background: "var(--gi-ivory-2)" }}>{t}</span>
                ))}
              </div>
            </Block>
          )}
          <Block title={t("book.trailer")}>
            <TrailerEmbed url={view.trailer_url} thumbnail={view.trailer_thumbnail_url} title={view.title} />
          </Block>
          {book.quotations?.length > 0 && (
            <Block title={t("book.quotations")}>
              <div className="space-y-6" data-testid="book-quotations">
                {book.quotations.filter(q => (q || "").trim()).map((q, i) => (
                  <blockquote key={`quote-${i}`} className="border-l-2 pl-6 italic text-lg" style={{ borderColor: "var(--gi-gold)", color: "var(--gi-charcoal-2)" }}>
                    {q}
                  </blockquote>
                ))}
              </div>
            </Block>
          )}
          {book.reviews?.length > 0 && (
            <Block title={t("book.reviews")}>
              <div className="space-y-6" data-testid="book-reviews">
                {book.reviews.filter(r => (r?.quote || "").trim()).map((r, i) => (
                  <figure key={`review-${i}`} className="border p-6" style={{ borderColor: "var(--gi-border)", background: "var(--gi-ivory-2)" }}>
                    <blockquote className="italic text-base" style={{ color: "var(--gi-charcoal-2)" }}>
                      &ldquo;{r.quote}&rdquo;
                    </blockquote>
                    <figcaption className="mt-4 text-sm" style={{ color: "var(--gi-maroon)" }}>
                      <strong>{r.reviewer || "Anonymous"}</strong>
                      {r.credential && <span style={{ color: "var(--gi-muted)" }}> · {r.credential}</span>}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </Block>
          )}
          <Block title="Bulk & Institutional Orders">
            <p>For libraries, schools and institutions, please write to us using the contact form.</p>
            <Link to="/contact" className="btn-outline mt-4" data-testid="book-bulk-order-btn">Enquire for Bulk Orders <ArrowRight size={14} /></Link>
          </Block>
          <Block title="Frequently Asked Questions" last>
            <FAQ q="Where can I buy the book?" a="Purchase links to Amazon, Green Mall and Goodreads will open in a new tab once the publisher supplies verified URLs for each edition." />
            <FAQ q="Is a sample available?" a="A downloadable sample PDF will be available on this page after publisher upload." />
            <FAQ q="Is the book available in Hindi or Bengali?" a="The architecture supports Hindi and Bengali editions. Each edition has its own cover, description, ISBN and purchase links. Translations will be published after human editorial review — automatic translation is not used." />
          </Block>
        </div>
      </section>

      {/* Related books — prefer same-series titles */}
      <section className="py-24" style={{ background: book.series === "Green Guru Knowledge Series" ? "var(--gi-green-soft)" : "var(--gi-ivory-2)" }}>
        <div className="container-wide">
          <div className="font-accent text-[10px]" style={{ color: book.series === "Green Guru Knowledge Series" ? "var(--gi-green)" : "var(--gi-gold)" }}>Continue Reading</div>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl leading-[1.02]" style={{ color: book.series === "Green Guru Knowledge Series" ? "var(--gi-green)" : "var(--gi-maroon)" }}>
            {book.series === "Green Guru Knowledge Series" ? "Related Gardening Books" : "Related Books"}
          </h2>
          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {related
              .filter(r => r.slug !== book.slug)
              .sort((a, b) => (a.series === book.series ? -1 : 1) - (b.series === book.series ? -1 : 1))
              .slice(0, 4)
              .map(r => (
                <Link key={r.id} to={`/books/${r.slug}`} className="block group" data-testid={`related-book-${r.slug}`}>
                  <BookCover title={r.title} accent={r.cover_accent} series={r.series} coverUrl={r.cover_url} />
                  <div className="mt-5 font-heading text-lg group-hover:underline" style={{ color: "var(--gi-charcoal)" }}>{r.title}</div>
                </Link>
              ))}
          </div>
        </div>
      </section>
      {reserveOpen && (
        <ReserveModal book={book} edition={{ language: edition }} onClose={() => setReserveOpen(false)} />
      )}
    </div>
  );
}

const Block = ({ title, children, last }) => (
  <div className={last ? "" : "mb-12"}>
    <div className="font-accent text-[10px] mb-4" style={{ color: "var(--gi-maroon)" }}>{title}</div>
    <div className="text-base leading-relaxed" style={{ color: "var(--gi-charcoal-2)" }}>{children}</div>
  </div>
);

const FAQ = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b py-4" style={{ borderColor: "var(--gi-divider)" }}>
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between text-left gap-4" data-testid={`faq-${q.slice(0, 12).toLowerCase().replace(/\s+/g, "-")}`}>
        <span className="font-heading text-lg" style={{ color: "var(--gi-charcoal)" }}>{q}</span>
        <ChevronRight size={16} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 200ms ease", color: "var(--gi-maroon)", flexShrink: 0 }} />
      </button>
      {open && <p className="mt-3 text-sm" style={{ color: "var(--gi-muted)" }}>{a}</p>}
    </div>
  );
};

// Purchase / action button with clear visual hierarchy.
// - primary: solid deep maroon (Amazon, Green Mall)
// - gold:    solid warm gold (Goodreads)
// - outline: maroon outline (Sample PDF)
// Placeholder state uses a dashed border + muted label instead of low-opacity
// solid fills — text remains fully readable.
const PurchaseBtn = ({ href, label, variant = "primary", icon, placeholder, testId }) => {
  const baseStyle = {
    width: "100%",
    padding: "13px 18px",
    fontFamily: "'Work Sans', sans-serif",
    fontWeight: 500,
    fontSize: "12px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    transition: "transform 200ms ease, background-color 200ms ease, border-color 200ms ease",
    borderRadius: "2px",
    cursor: placeholder ? "not-allowed" : "pointer",
  };

  const activeStyles = {
    primary: { background: "var(--gi-maroon)", color: "var(--gi-ivory)", border: "1px solid var(--gi-maroon)" },
    gold: { background: "var(--gi-gold)", color: "var(--gi-charcoal)", border: "1px solid var(--gi-gold)" },
    outline: { background: "transparent", color: "var(--gi-maroon)", border: "1px solid var(--gi-maroon)" },
  };

  const placeholderStyles = {
    primary: { background: "#FFFFFF", color: "var(--gi-maroon)", border: "1px dashed var(--gi-maroon)" },
    gold: { background: "#FFFFFF", color: "#8A6A00", border: "1px dashed var(--gi-gold)" },
    outline: { background: "#FFFFFF", color: "var(--gi-maroon)", border: "1px dashed var(--gi-maroon)" },
  };

  const style = { ...baseStyle, ...(placeholder ? placeholderStyles[variant] : activeStyles[variant]) };
  const trailingIcon = icon || <ExternalLink size={14} />;

  if (placeholder) {
    // Rule: never expose an "Awaiting URL" stub to public visitors.
    // Hide the button entirely until the publisher supplies a real link.
    return null;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={style}
      data-testid={testId}
    >
      {label} {trailingIcon}
    </a>
  );
};
